import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { IChatConversation, INewConversationData, IOpenConversationManageUsersData } from '@fi-shared/fi-chat/fi-chat';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { ManageUsersStep } from '@fi-shared/fi-chat/fi-chat-conversation-manage-type';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';

@Component({
    selector: 'fi-chat-conversation-container',
    templateUrl: './fi-chat-conversation-container.component.html',
    styleUrls: ['./fi-chat-conversation-container.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-container' },
    standalone: false,
})
export class FiChatConversationContainerComponent implements OnInit, OnDestroy {
    @Input() conversation: IChatConversation;
    @Input() currentManagingUsersStep: ManageUsersStep;
    @Input() managingUsersMode: boolean = false;
    @Output() closeConversation = new EventEmitter<IChatConversation>();
    @Output() startNewConversation = new EventEmitter<INewConversationData>();
    protected readonly profileSizes = FiProfileSizes;

    constructor(
        private fiChatService: FiChatService,
        private fiChatHelperService: FiChatHelperService,
        private fiChatSyncService: FiChatSyncService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.subscribeToConversationUpdates();
        if (this.conversation.IsHidden) this.updateConversationIsHidden(false);
    }

    private subscribeToConversationUpdates(): void {
        this.fiChatSyncService
            .conversationUpdated$(this.conversation.Id)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.cdr.markForCheck();
            });
    }

    protected isForwardConversationType(): boolean {
        return this.conversation.Type === FiChatConversationType.Forward;
    }

    protected handleOnConversationImageClick(): void {
        if (this.isGroupChat()) this.setManagingUsersMode({ status: true, currentStep: ManageUsersStep.Remove });
    }

    protected onCloseConversation(): void {
        this.closeConversation.emit(this.conversation);
    }

    protected onStartNewConversation($event: INewConversationData): void {
        this.startNewConversation.emit($event);
    }

    protected isNewConversation(): boolean {
        return this.fiChatHelperService.isConversationNew(this.conversation);
    }

    protected isGroupChat(): boolean {
        return this.fiChatHelperService.isGroupChat(this.conversation);
    }

    private updateConversationIsHidden(newIsHidden: boolean): void {
        this.fiChatService.updateConversationIsHidden(this.conversation.Id, newIsHidden);
    }

    protected closeManageUsersMode(): void {
        this.setManagingUsersMode({ status: false, currentStep: ManageUsersStep.Remove });
    }

    protected setManagingUsersMode(manageUsersModeData: IOpenConversationManageUsersData): void {
        this.managingUsersMode = manageUsersModeData.status;
        this.currentManagingUsersStep = manageUsersModeData.currentStep;
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
